import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MessagingMapService } from '../../services/messaging-map.service';
import { StrategyRoute, normalizeCampaignId, routeAriaLabel, routeLaneText, routesForCampaign } from './strategy-route.logic';

/**
 * Shows which Messaging Map script a sequence's campaign is linked to, with
 * the script's lead felt need, as a link to /strategy. Renders nothing when
 * the feature is off for the company, when no script links the campaign, or
 * when the lookup fails (the chip is a pointer, not data the page needs).
 *
 * `companyId` must be the sequence's own company (its record's partition
 * key), so the campaign id and the scripts always come from one company.
 */
@Component({
  selector: 'app-strategy-route-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (routes.length) {
      <div class="route-chips">
        @for (route of routes; track route.scriptId) {
          <a class="route-chip" [href]="href" [attr.aria-label]="ariaLabel(route)" (click)="open($event)">
            <i class="pi pi-sitemap" aria-hidden="true"></i>
            <span class="route-prefix">Messaging Map:</span>
            <span class="route-script">{{ route.scriptName }}</span>
            <span class="route-lane" [class.route-lane-missing]="route.laneStatus !== 'on_map'">· {{ laneText(route) }}</span>
          </a>
        }
      </div>
    }
  `,
  styles: [`
    .route-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }
    .route-chip {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      max-width: 100%;
      padding: 0.25rem 0.75rem;
      border-radius: 1rem;
      border: 1px solid var(--p-content-border-color, #e5e7eb);
      background: var(--p-content-background, #ffffff);
      color: var(--p-text-color, #1f2937);
      font-size: 0.85rem;
      text-decoration: none;
      overflow-wrap: anywhere;
    }
    .route-chip:hover {
      background: var(--p-content-hover-background, #f3f4f6);
    }
    .route-chip:focus-visible {
      outline: 2px solid var(--p-primary-color, #3a8fa0);
      outline-offset: 2px;
    }
    .route-prefix {
      color: var(--p-text-muted-color, #6b7280);
    }
    .route-script {
      font-weight: 600;
    }
    .route-lane-missing {
      color: var(--p-text-muted-color, #6b7280);
    }
  `]
})
export class StrategyRouteChipComponent implements OnChanges {
  @Input() companyId: string | null | undefined = null;
  @Input() providerCampaignId: unknown = null;

  routes: StrategyRoute[] = [];
  /** Bumped per lookup so an answer for an older input is dropped. */
  private lookup = 0;

  constructor(
    private messagingMapService: MessagingMapService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(): void {
    void this.load();
  }

  laneText(route: StrategyRoute): string {
    return routeLaneText(route);
  }

  ariaLabel(route: StrategyRoute): string {
    return routeAriaLabel(route);
  }

  /** Plain href for new-tab clicks; /strategy selects the company from the query. */
  get href(): string {
    return this.companyId ? `/strategy?companyId=${encodeURIComponent(this.companyId)}` : '/strategy';
  }

  open(event: MouseEvent): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    void this.router.navigate(['/strategy'], {
      queryParams: this.companyId ? { companyId: this.companyId } : {},
      queryParamsHandling: 'merge'
    });
  }

  private async load(): Promise<void> {
    const lookup = ++this.lookup;
    const companyId = this.companyId;
    const campaignId = normalizeCampaignId(this.providerCampaignId);
    this.show(lookup, []);
    if (!companyId || !campaignId) {
      return;
    }
    try {
      if (!(await this.messagingMapService.isEnabled(companyId)) || lookup !== this.lookup) {
        return;
      }
      const { scripts } = await this.messagingMapService.getScripts(companyId);
      if (lookup !== this.lookup || !scripts.some(script => normalizeCampaignId(script.providerCampaignId) === campaignId)) {
        return;
      }
      const { enabled, map } = await this.messagingMapService.getMap(companyId);
      this.show(lookup, enabled ? routesForCampaign(scripts, map, campaignId) : []);
    } catch (error) {
      console.warn('Messaging Map route lookup failed', error);
      this.show(lookup, []);
    }
  }

  private show(lookup: number, routes: StrategyRoute[]): void {
    if (lookup !== this.lookup) {
      return;
    }
    this.routes = routes;
    this.cdr.markForCheck();
  }
}
